import { useRef, useState } from 'react'
import { Alert, Button, Card, Checkbox, Group, Modal, Select, SegmentedControl, SimpleGrid, Stack, Text, TextInput, Title } from '@mantine/core'
import { useSelector } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'
import backend from '../../backend/backend'
import DataList from '../../components/shared/DataList'
import Money from '../../components/shared/Money'
import PageHeader from '../../components/shared/PageHeader'
import { ReasonModal, ScheduleTable } from '../../components/shared/Pieces'
import StatusBadge from '../../components/shared/StatusBadge'
import Stat from '../../components/shared/Stat'
import { useAction, useCan, useResource, useT } from '../../hooks'
import Message from '../../i18n/Message'
import { keys, loadDue, loadLoan, loadLoans, loadOverdue, loadPayments } from '../../store/dataActionCreators'
import { displayPhone, formatDate, formatDateTime, naira, toKobo } from '../../utils/format'

const loanColumns = [
  { key: 'loan_no', header: 'col_ref', ref: true },
  { key: 'customer_name', header: 'col_name' },
  { key: 'outstanding_kobo', header: 'col_outstanding', render: (l) => <Money kobo={l.outstanding_kobo} /> },
  { key: 'overdue_amount_kobo', header: 'col_overdue', render: (l) => (l.overdue_amount_kobo ? <Money kobo={l.overdue_amount_kobo} c="red" /> : '—') },
  { key: 'next_due_date', header: 'col_next_due', render: (l) => formatDate(l.next_due_date), mobileHidden: true },
  { key: 'status', header: 'col_status', render: (l) => <StatusBadge status={l.status} /> },
]

export function LoansList({ base }) {
  const navigate = useNavigate()
  const [status, setStatus] = useState('all')
  const t = useT()
  const { data, loading } = useResource(keys.loans(status === 'all' ? '' : status), () => loadLoans(status === 'all' ? '' : status))
  return (
    <>
      <PageHeader title="loans_title" />
      <SegmentedControl mb="md" value={status} onChange={setStatus} style={{ maxWidth: '100%', overflowX: 'auto' }}
        data={['all', 'awaiting_release', 'active', 'defaulted', 'completed'].map((s) => ({ value: s, label: s === 'all' ? t('filter_all') : t(`status_${s}`) }))} />
      <DataList items={data?.items} columns={loanColumns} loading={loading} empty="loans_empty" onRowClick={(l) => navigate(`${base}/loans/${l.id}`)} />
    </>
  )
}

/** Agent: due and overdue accounts. Admin: overdue, defaults and penalties. */
export function DueList({ base, overdueOnly }) {
  const navigate = useNavigate()
  const due = useResource(overdueOnly ? keys.overdue : keys.due, overdueOnly ? loadOverdue : loadDue)
  const items = due.data?.items
  return (
    <>
      <PageHeader title={overdueOnly ? 'overdue_title' : 'repayments_title'} />
      {overdueOnly && items && (
        <SimpleGrid cols={{ base: 2, sm: 3 }} mb="lg">
          <Stat label="stat_overdue" value={items.length} />
          <Stat label="stat_overdue_amount" kobo={items.reduce((a, l) => a + l.overdue_amount_kobo, 0)} />
          <Stat label="stat_defaulted" value={items.filter((l) => l.status === 'defaulted').length} />
        </SimpleGrid>
      )}
      <DataList items={items} columns={loanColumns} loading={due.loading} empty="repayments_empty" onRowClick={(l) => navigate(`${base}/loans/${l.id}`)} />
    </>
  )
}

export function RecordPaymentModal({ loan, opened, onClose, onDone }) {
  const t = useT()
  const isAdmin = useSelector((s) => s.auth.user?.role !== 'agent')
  const key = useRef(crypto.randomUUID())
  const { busy, run } = useAction()
  const preDeposit = loan.status === 'awaiting_release'
  const [kind, setKind] = useState(preDeposit ? 'deposit' : 'installment')
  const [method, setMethod] = useState('cash')
  const [amount, setAmount] = useState('')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [confirmDuplicate, setConfirmDuplicate] = useState(false)
  const [duplicate, setDuplicate] = useState(false)
  const kobo = toKobo(amount)
  const submit = async () => {
    const res = await run(() => backend.recordPayment({
      loanId: loan.id, kind, method, amountKobo: kobo, reference, note, idempotencyKey: key.current, confirmDuplicate,
    }).catch((err) => {
      if (err?.code === 'possible_duplicate') setDuplicate(true)
      throw err
    }), { success: isAdmin ? 'payment_recorded_verified' : 'payment_recorded_pending', onDone })
    if (res) {
      key.current = crypto.randomUUID()
      setAmount('')
      setReference('')
      onClose()
    }
  }
  return (
    <Modal opened={opened} onClose={onClose} title={<Message id="record_payment" />} centered>
      <Stack>
        {!isAdmin && (
          <Alert color="brand"><Message id="payment_pending_note" /></Alert>
        )}
        <Select label={t('field_payment_kind')} value={kind} onChange={setKind} allowDeselect={false}
          data={(preDeposit ? ['deposit'] : ['installment', 'settlement']).map((k) => ({ value: k, label: t(`kind_${k}`) }))} />
        <Select label={t('field_method')} value={method} onChange={setMethod} allowDeselect={false}
          data={['cash', 'bank_transfer', 'pos'].map((m) => ({ value: m, label: t(`method_${m}`) }))} />
        <TextInput label={t('field_amount')} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.currentTarget.value)}
          description={!preDeposit ? t('outstanding_hint', { amount: naira(loan.outstanding_kobo) }) : t('deposit_due_hint', { amount: naira(loan.deposit_required_kobo - loan.deposit_paid_kobo) })} />
        <TextInput label={t('field_reference')} value={reference} onChange={(e) => setReference(e.currentTarget.value)} />
        <TextInput label={t('field_note')} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        {duplicate && isAdmin && <Checkbox label={t('payment_confirm_duplicate')} checked={confirmDuplicate} onChange={(e) => setConfirmDuplicate(e.currentTarget.checked)} />}
        <Button loading={busy} disabled={Number.isNaN(kobo) || kobo <= 0} onClick={submit}>
          <Message id="record_payment" />
        </Button>
      </Stack>
    </Modal>
  )
}

export function LoanDetail({ base }) {
  const { id } = useParams()
  const t = useT()
  const { data, reload } = useResource(keys.loan(id), () => loadLoan(id))
  const { busy, run } = useAction()
  const canRelease = useCan('loans.release')
  const canDefault = useCan('loans.default')
  const canWaive = useCan('penalty.waive')
  const canVerify = useCan('payments.verify')
  const canReverse = useCan('payments.reverse')
  const canRecord = useCan('payments.record')
  const canLink = useCan('payments.generate_link')
  const paystack = useSelector((s) => s.auth.config?.paystackConfigured)
  const [modal, setModal] = useState(null)
  const [release, setRelease] = useState('')
  const [target, setTarget] = useState(null)
  const [linkAmount, setLinkAmount] = useState('')
  const [link, setLink] = useState(null)
  if (!data) return null
  const { loan, installments, payments, receipts } = data
  const receiptFor = (pid) => receipts.find((r) => r.payment_id === pid)
  const loanAct = (action, body, success) => run(() => backend.loanAction(loan.id, action, body), { success, onDone: reload })
  const payAct = (pid, action, body, success) => run(() => backend.paymentAction(pid, action, body), { success, onDone: reload })

  return (
    <>
      <PageHeader title="loan_title" subtitle={`${loan.loan_no} · ${loan.customer_name}`}
        actions={
          <>
            <StatusBadge status={loan.status} />
            <Button variant="default" component={Link} to={`/print/statement/${loan.id}`} target="_blank"><Message id="statement" /></Button>
          </>
        } />
      <SimpleGrid cols={{ base: 2, md: 4 }} mb="lg">
        <Stat label="quote_total" kobo={loan.total_kobo} />
        <Stat label="col_paid" kobo={loan.paid_kobo} />
        <Stat label="col_outstanding" kobo={loan.outstanding_kobo} />
        <Stat label="col_overdue" kobo={loan.overdue_amount_kobo} />
      </SimpleGrid>
      <Text size="sm" c="dimmed" mb="md">
        {displayPhone(loan.customer_phone)} · <Message id="col_next_due" />: {formatDate(loan.next_due_date)}
        {loan.days_overdue > 0 && <> · <Message id="days_overdue" values={{ count: loan.days_overdue }} /></>}
        {loan.device_serial && <> · {loan.device_serial}</>}
      </Text>

      <Group mb="lg">
        {canRecord && ['awaiting_release', 'active', 'defaulted'].includes(loan.status) && (
          <Button onClick={() => setModal('pay')}><Message id="record_payment" /></Button>
        )}
        {canLink && paystack && ['active', 'defaulted'].includes(loan.status) && (
          <Button variant="default" onClick={() => setModal('link')}><Message id="payment_link" /></Button>
        )}
        {canRelease && loan.status === 'awaiting_release' && (
          <Button color="green" onClick={() => setModal('release')}><Message id="release_loan" /></Button>
        )}
        {canDefault && loan.status === 'active' && <Button color="red" variant="light" onClick={() => setModal('default')}><Message id="mark_default" /></Button>}
        {canDefault && loan.status === 'defaulted' && <Button variant="light" onClick={() => setModal('reinstate')}><Message id="reinstate" /></Button>}
      </Group>

      {loan.status === 'awaiting_release' && (
        <Alert color="yellow" mb="lg">
          <Message id="release_waiting" values={{ paid: naira(loan.deposit_paid_kobo), required: naira(loan.deposit_required_kobo) }} />
        </Alert>
      )}

      {installments.length > 0 && (
        <Card mb="lg">
          <Title order={5} mb="sm"><Message id="schedule_title" /></Title>
          <ScheduleTable installments={installments} />
          {canWaive && installments.filter((i) => i.late_fee_kobo > i.late_fee_waived_kobo).map((i) => (
            <Button key={i.id} mt="sm" size="xs" variant="light" onClick={() => { setTarget(i); setModal('waive') }}>
              <Message id="waive_penalty" values={{ n: i.seq }} />
            </Button>
          ))}
        </Card>
      )}

      <Title order={4} mb="sm"><Message id="payments_title" /></Title>
      <DataList items={payments} empty="payments_empty"
        columns={[
          { key: 'reference', header: 'col_ref', ref: true },
          { key: 'kind', header: 'col_type', render: (p) => <Message id={`kind_${p.kind}`} /> },
          { key: 'method', header: 'col_method', render: (p) => <Message id={`method_${p.method}`} />, mobileHidden: true },
          { key: 'amount_kobo', header: 'col_amount', render: (p) => <Money kobo={p.amount_kobo} /> },
          { key: 'status', header: 'col_status', render: (p) => <StatusBadge status={p.status} /> },
          { key: 'paid_at', header: 'col_date', render: (p) => formatDateTime(p.paid_at), mobileHidden: true },
          { key: 'actions', header: 'col_actions', render: (p) => (
            <Group gap={6}>
              {p.status === 'pending' && canVerify && <Button size="compact-xs" loading={busy} onClick={() => payAct(p.id, 'verify', {}, 'payment_verified')}><Message id="verify" /></Button>}
              {p.status === 'pending' && canVerify && <Button size="compact-xs" color="red" variant="light" onClick={() => { setTarget(p); setModal('reject') }}><Message id="reject" /></Button>}
              {p.status === 'verified' && receiptFor(p.id) && <Button size="compact-xs" variant="default" component={Link} to={`/print/receipt/${p.id}`} target="_blank"><Message id="receipt" /></Button>}
              {p.status === 'verified' && canReverse && <Button size="compact-xs" color="red" variant="subtle" onClick={() => { setTarget(p); setModal('reverse') }}><Message id="reverse" /></Button>}
            </Group>
          ) },
        ]} />

      <RecordPaymentModal key={modal === 'pay' ? 'open' : 'closed'} loan={loan} opened={modal === 'pay'} onClose={() => setModal(null)} onDone={reload} />
      <Modal opened={modal === 'release'} onClose={() => setModal(null)} title={<Message id="release_loan" />} centered>
        <Stack>
          <TextInput label={t(loan.kind === 'device' ? 'field_serial' : 'field_disbursement_ref')} value={release} onChange={(e) => setRelease(e.currentTarget.value)} />
          <Button loading={busy} disabled={!release.trim()} onClick={async () => {
            await loanAct('release', loan.kind === 'device' ? { deviceSerial: release.trim() } : { disbursementReference: release.trim() }, 'loan_released')
            setModal(null)
          }}><Message id="release_loan" /></Button>
        </Stack>
      </Modal>
      <Modal opened={modal === 'link'} onClose={() => { setModal(null); setLink(null) }} title={<Message id="payment_link" />} centered>
        <Stack>
          <TextInput label={t('field_amount')} inputMode="decimal" value={linkAmount} onChange={(e) => setLinkAmount(e.currentTarget.value)} />
          <Button loading={busy} onClick={async () => {
            const res = await run(() => backend.paymentLink({ loanId: loan.id, kind: 'installment', amountKobo: toKobo(linkAmount) }))
            if (res) setLink(res.url)
          }}><Message id="payment_link_create" /></Button>
          {link && <TextInput readOnly value={link} onFocus={(e) => e.currentTarget.select()} />}
        </Stack>
      </Modal>
      <ReasonModal opened={modal === 'default'} title="mark_default" busy={busy} onClose={() => setModal(null)} onSubmit={async (r) => { await loanAct('default', { reason: r }, 'loan_defaulted'); setModal(null) }} />
      <ReasonModal opened={modal === 'reinstate'} title="reinstate" busy={busy} onClose={() => setModal(null)} onSubmit={async (r) => { await loanAct('reinstate', { reason: r }, 'loan_reinstated'); setModal(null) }} />
      <ReasonModal opened={modal === 'waive'} title="waive_penalty_title" busy={busy} onClose={() => setModal(null)} onSubmit={async (r) => { await loanAct('waive-penalty', { installmentId: target.id, reason: r }, 'penalty_waived'); setModal(null) }} />
      <ReasonModal opened={modal === 'reject'} title="reject" label="note" busy={busy} onClose={() => setModal(null)} onSubmit={async (r) => { await payAct(target.id, 'reject', { note: r }, 'payment_rejected'); setModal(null) }} />
      <ReasonModal opened={modal === 'reverse'} title="reverse" busy={busy} onClose={() => setModal(null)} onSubmit={async (r) => { await payAct(target.id, 'reverse', { reason: r }, 'payment_reversed'); setModal(null) }} />
    </>
  )
}

export function PaymentsQueue() {
  const t = useT()
  const navigate = useNavigate()
  const [status, setStatus] = useState('pending')
  const { data, loading, reload } = useResource(keys.payments(status), () => loadPayments(status))
  const { busy, run } = useAction()
  const canVerify = useCan('payments.verify')
  return (
    <>
      <PageHeader title="payments_page_title" />
      <SegmentedControl mb="md" value={status} onChange={setStatus}
        data={['pending', 'verified', 'rejected', 'reversed'].map((s) => ({ value: s, label: t(`status_${s}`) }))} />
      <DataList items={data?.items} loading={loading} empty="payments_empty" onRowClick={(p) => navigate(`/admin/loans/${p.loan_id}`)}
        columns={[
          { key: 'reference', header: 'col_ref', ref: true },
          { key: 'customer_name', header: 'col_name' },
          { key: 'loan_no', header: 'col_loan', mobileHidden: true },
          { key: 'amount_kobo', header: 'col_amount', render: (p) => <Money kobo={p.amount_kobo} /> },
          { key: 'method', header: 'col_method', render: (p) => <Message id={`method_${p.method}`} />, mobileHidden: true },
          { key: 'status', header: 'col_status', render: (p) => <StatusBadge status={p.status} /> },
          { key: 'actions', header: 'col_actions', render: (p) => p.status === 'pending' && canVerify && (
            <Button size="compact-xs" loading={busy} onClick={(e) => { e.stopPropagation(); run(() => backend.paymentAction(p.id, 'verify', {}), { success: 'payment_verified', onDone: reload }) }}><Message id="verify" /></Button>
          ) },
        ]} />
    </>
  )
}

